import {HoistModel, managed, TaskObserver, XH} from '@xh/hoist/core';
import {bindable, observable} from '@xh/hoist/mobx';
import {InstrumentSelectedParams} from '../SecMasterTabModel';
import {VirtualGridModel} from '../../../cmp/virtualGrid/VirtualGridModel';
import {ColDef, FilterModel, GetContextMenuItemsParams, JoinAdvancedFilterModel} from 'ag-grid-community';
import {Roles, Security} from '../../../data/DataTypes';
import {fmtDateTimeSec, fmtPrice} from '@xh/hoist/format';
import {first} from 'lodash';
import {SecurityImportDialogModel} from './import/SecurityImportDialogModel';
import {Icon} from '@xh/hoist/icon';
import {SecurityEditDialogModel} from './edit/SecurityEditDialogModel';
import {currencyCodes, exchanges, sectors} from '../DomainData';
import {secExchangeBadge, secSubTypeBadge} from '../../../cmp/secBadges/SecBadges';
import {span} from '@xh/hoist/cmp/layout';
import {SplitProcessorModel} from './splits/SplitProcessorModel';

export class SecurityGridModel extends HoistModel {
    @managed apiTask = TaskObserver.trackLast();
    @managed @observable accessor vGridModel: VirtualGridModel<Security>;
    @managed importModel = new SecurityImportDialogModel();
    @managed editModel = new SecurityEditDialogModel();
    @managed splitProcessor = new SplitProcessorModel();
    @bindable accessor quickFilter;
    @observable accessor selectedRecord: Security;
    private securityType: InstrumentSelectedParams;

    constructor() {
        super();

        this.addReaction({
            track: () => this.quickFilter,
            run: () => this.processQuickFilter()
        });
    }

    setVGridModel(vGridModel: VirtualGridModel<Security>) {
        this.vGridModel = vGridModel;

        this.addReaction({
            track: () => this.vGridModel.selectedRecords,
            run: records => (this.selectedRecord = first(records))
        });
    }

    setSecurityType(params: InstrumentSelectedParams) {
        this.securityType = params;

        if (this.quickFilter) this.quickFilter = null;
        this.setDefaultFilter();
    }

    clearFilters() {
        if (this.quickFilter) this.quickFilter = null;

        this.setDefaultFilter();
    }

    get vGridQueryUrl(): string {
        return `${XH.getConf('svcApiUrl')}/secmaster/queryData`;
    }

    get vGridColumns(): ColDef[] {
        return [
            {field: 'id', hide: true},
            {
                field: 'type',
                hide: true,
                cellDataType: 'enum',
                filter: 'agTextColumnFilter',
                filterParams: {readOnly: true}
            },
            {
                field: 'subType',
                width: 100,
                cellDataType: 'enum',
                filter: 'agTextColumnFilter',
                filterParams: {readOnly: true},
                cellRenderer: ({value}) => secSubTypeBadge(value)
            },
            {field: 'ticker', width: 100, filter: 'agTextColumnFilter'},
            {field: 'name', width: 300, filter: 'agTextColumnFilter'},
            {
                field: 'currency',
                width: 100,
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: currencyCodes
                }
            },
            {field: 'country', headerName: 'Country Code', width: 100},
            {
                field: 'sector',
                width: 100,
                filter: 'agSetColumnFilter',
                filterParams: {values: sectors}
            },
            {field: 'industry', width: 100},
            {
                field: 'exchange',
                width: 100,
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: exchanges
                },
                cellRenderer: ({value}) => (value ? secExchangeBadge(value) : null)
            },
            {
                field: 'lastDividend',
                cellDataType: 'number',
                type: 'rightAligned',
                cellClass: 'security__numeric-cell',
                cellRenderer: ({value}) => {
                    if (value == 0.0001) return span({item: '> 0', className: 'sec-plug-value'});
                    if (value && value != 0) return fmtPrice(value, {precision: 2, prefix: '$'});
                    return '';
                }
            },
            {
                field: 'beta',
                cellDataType: 'number',
                type: 'rightAligned',
                cellClass: 'security__numeric-cell',
                cellRenderer: ({value}) => {
                    if (value == 0.0001) return span({item: '> 0', className: 'sec-plug-value'});
                    if (value && value != 0) return value.toFixed(4);
                    return '';
                }
            },
            {field: 'updatedBy'},
            {field: 'updated', cellRenderer: ({value}) => fmtDateTimeSec(value)}
        ];
    }

    get vGridColumnDefaults(): ColDef {
        return {
            cellDataType: 'text',
            sortable: true
        };
    }

    vGridCtxMenu(params: GetContextMenuItemsParams) {
        return ['copy', 'copyWithHeaders', 'separator', 'excelExport', 'csvExport', 'separator', 'autoSizeAll', 'columnChooser', 'resetColumns'];
    }

    onRowDoubleClicked(security: Security) {
        this.edit();
    }

    get gridFilter() {
        //TODO: impl
        return null;
    }

    async add() {
        if (!XH.entitlementService.hasRole(Roles.SEC_MASTER_MANAGER)) {
            XH.dangerToast('You do not have permission to add securities.');
            return;
        }
        await this.editModel.openAsync(null);
        this.vGridModel.refreshData();
    }

    async edit() {
        await this.editModel.openAsync(this.selectedRecord);
        this.vGridModel.refreshData();
    }

    async clone() {
        if (!XH.entitlementService.hasRole(Roles.SEC_MASTER_MANAGER)) {
            XH.dangerToast('You do not have permission to clone securities.');
            return;
        }

        await this.editModel.openAsync({...this.selectedRecord, id: 0, ticker: null, name: null});
        this.vGridModel.refreshData();
    }

    async delete() {
        if (!XH.entitlementService.hasRole(Roles.SEC_MASTER_MANAGER)) {
            XH.dangerToast('You do not have permission to delete securities.');
            return;
        }

        const confirm = await XH.confirm({
            message: `Are you sure you want to delete ${this.selectedRecord.ticker}?`,
            icon: Icon.warning(),
            title: 'User confirmation required'
        });
        if (!confirm) return;

        await XH.secMasterService.deleteSecurityAsync(this.selectedRecord.id).catchDefault({alertType: 'dialog'}).linkTo(this.apiTask);
        this.vGridModel.refreshData();
    }

    openSplits() {
        this.splitProcessor.open();
    }

    async updateExisting() {
        if (!XH.entitlementService.hasRole(Roles.SEC_MASTER_MANAGER)) {
            XH.dangerToast('You do not have permission to refresh securities.');
            return;
        }

        await XH.secMasterService.refreshSecurityAsync(this.selectedRecord.id).catchDefault({alertType: 'toast'}).linkTo(this.apiTask);
        this.vGridModel.refreshData();
    }

    import() {
        this.importModel.openAsync().then(result => {
            if (result) this.vGridModel.refreshData();
        });
    }

    private setDefaultFilter() {
        const filterMode: FilterModel = {};
        if (this.securityType?.type) filterMode.type = {filterType: 'text', type: 'equals', filter: this.securityType?.type};
        if (this.securityType?.subType)
            filterMode.subType = {
                filterType: 'text',
                type: 'equals',
                filter: this.securityType?.subType
            };

        this.vGridModel.setFilter(filterMode);
    }

    private processQuickFilter() {
        const filter = this.quickFilter;
        if (!this.quickFilter) {
            this.vGridModel.setAdvanedFilter(null);
            this.setDefaultFilter();
            return;
        }

        const advancedFilterModel: JoinAdvancedFilterModel = {
            type: 'OR',
            filterType: 'join',
            conditions: [
                {colId: 'name', filterType: 'text', type: 'contains', filter},
                {colId: 'ticker', filterType: 'text', type: 'contains', filter}
            ]
        };

        this.vGridModel.setAdvanedFilter(advancedFilterModel);
    }
}
